import React, { Component } from "react";
import Button from "../components/Button";
import Close from "../icons/Close";
import DropdownMenu, {
  DropdownMenuDivider,
  DropdownMenuItem,
} from "../components/DropdownMenu";
import { isEmpty } from "lodash";

import "leaflet";
import "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";

// @ts-ignore
import gpsi from "geojson-polygon-self-intersections";
import * as turfArea from "@turf/area";
import { style, cssRule } from "typestyle";
import { LatLng, Layer, LeafletEvent } from "leaflet";
import { Feature, Point } from "geojson";

const L = window.L;

cssRule(".leaflet-marker-icon", { borderRadius: 10 });

interface State {
  isCreating: boolean;
  geoJson: Feature;
  currentAreaUnitIsAcres: boolean;
}

interface Props {
  clearAreaDisabled?: boolean;
  onChange: (value: Feature | null) => void;
  map: L.Map;
  value: Feature;
  allowSelfIntersection?: boolean;
}

export default class MapPolygonEditControl extends Component<Props, State> {
  drawControl: L.Control;
  feature: L.FeatureGroup;

  static defaultProps: Partial<Props> = {
    clearAreaDisabled: false,
    onChange: () => null,
    allowSelfIntersection: false,
  };

  static polygonHasSelfIntersections(polygon: Feature): boolean {
    const intersectionPolyline = gpsi(polygon);
    return !!intersectionPolyline.geometry.coordinates.length;
  }

  handleFeatureCreated = (event: LeafletEvent) => {
    const geoJson = event.layer.toGeoJSON();
    this.setState({
      isCreating: false,
    });
    this.props.onChange(geoJson);
    this.renderGeoJson();
  };

  handlePolygonEdit = ({ target }: LeafletEvent) => {
    const geoJson = target.toGeoJSON();
    const hasSelfIntersections =
      MapPolygonEditControl.polygonHasSelfIntersections(geoJson);
    if (!this.props.allowSelfIntersection && hasSelfIntersections) {
      this.renderGeoJson();
    } else {
      this.props.onChange(geoJson);
    }
  };

  handleDrawButtonClick = () => {
    // @ts-ignore HACK: draw control does'nt provide draw API
    this.drawControl._toolbars.draw._modes.polygon.handler.enable();
    this.setState({
      isCreating: true,
    });
  };

  handleCancelButtonClick = () => {
    // @ts-ignore HACK: draw control does'nt provide draw API
    this.drawControl._toolbars.draw._modes.polygon.handler.disable();
    this.setState({
      isCreating: false,
    });
  };

  handleAcresButtonClick = () => {
    this.setState({
      currentAreaUnitIsAcres: true,
    });
  };

  handleHectaresButtonClick = () => {
    this.setState({
      currentAreaUnitIsAcres: false,
    });
  };

  handleClearButtonClick = () => {
    this.feature.clearLayers();
    this.setState({
      isCreating: false,
    });
    this.props.onChange(null);
  };

  UNSAFE_componentWillMount() {
    this.feature = L.featureGroup([]);
    this.props.map.addLayer(this.feature);

    this.drawControl = new L.Control.Draw({
      draw: {
        marker: false,
        polyline: false,
        circle: false,
        rectangle: false,
        polygon: {
          allowIntersection: false,
        },
      },
      edit: {
        featureGroup: this.feature,
      },
    });
    this.props.map.addControl(this.drawControl);
    L.DomUtil.addClass(
      this.drawControl.getContainer() as HTMLElement,
      style({ display: "none" }),
    );

    this.props.map.on(L.Draw.Event.CREATED, this.handleFeatureCreated);

    this.setState({
      isCreating: false,
      currentAreaUnitIsAcres: true,
    });
  }

  componentDidMount() {
    this.renderGeoJson(true);
  }

  componentDidUpdate() {
    this.renderGeoJson(false);
  }

  renderGeoJson(fitBounds?: boolean) {
    const geoJson = this.props.value;
    if (isEmpty(geoJson)) {
      return;
    }

    const { map } = this.props;
    this.feature.getLayers().map((layer) => layer.off("edit"));
    this.feature.clearLayers();

    const geoJsonLayer = L.geoJSON(geoJson, {
      // For some reason this is not described in types
      // but removing it will break site area editor
      // @ts-ignore
      editable: true,
      onEachFeature: (_: Feature, layer: L.Layer) => {
        layer.on("edit", (...args) => {
          setTimeout(() => {
            this.handlePolygonEdit(args[0]);
          }, 0);
        });
      },
    });

    const intersectionMultiPointFeature = gpsi(geoJson);
    const intersectionLayer = L.geoJSON(intersectionMultiPointFeature, {
      pointToLayer: function (
        geoJsonPoint: Feature<Point>,
        latlng: LatLng,
      ): Layer {
        const alertIcon = L.icon({
          iconUrl: "icon-intersection.svg",
          iconSize: [80, 80],
          iconAnchor: [40, 40],
        });
        return L.marker(latlng, {
          icon: alertIcon,
          zIndexOffset: 9999999,
        }).bindTooltip(() => "Self-intersection, please fix to save site area");
      },
    });

    // @ts-ignore
    window.geoJsonLayer = geoJsonLayer;

    // TODO: handle layer remove; detach events
    this.feature.addLayer(geoJsonLayer);
    this.feature.addLayer(intersectionLayer);
    if (fitBounds) {
      map.fitBounds(geoJsonLayer.getBounds());
    }
  }

  render() {
    const { value } = this.props;
    const { isCreating } = this.state;

    if (isCreating) {
      return this.renderCancelButton();
    }

    if (isEmpty(value)) {
      return this.renderDrawButton();
    }

    return this.renderAreaDropdown();
  }

  renderDrawButton() {
    return (
      <Button
        onClick={this.handleDrawButtonClick}
        type="control"
        icon={<img src={"ic-area.svg"} />}
        label="Select scan area"
      />
    );
  }

  renderCancelButton() {
    return (
      <Button
        onClick={this.handleCancelButtonClick}
        type="primary"
        icon={<Close size="14" />}
      />
    );
  }

  renderAreaDropdown() {
    const { clearAreaDisabled, value } = this.props;

    const area = turfArea.default(value);
    const acresString = `${(area * 0.000247104).toFixed(2)} AC`;
    const hectaresString = `${(area * 0.0001).toFixed(2)} HA`;

    const dropdownTitle = (
      <div className={style({ display: "flex", flexDirection: "row" })}>
        <span>
          {this.state.currentAreaUnitIsAcres ? acresString : hectaresString}
        </span>
        <img src="ic-more-vert.svg" />
      </div>
    );

    return (
      <DropdownMenu title={dropdownTitle}>
        <DropdownMenuItem onClick={this.handleAcresButtonClick} label="Acres" />
        <DropdownMenuItem
          onClick={this.handleHectaresButtonClick}
          label="Hectares"
        />
        {!clearAreaDisabled && <DropdownMenuDivider />}
        {!clearAreaDisabled && (
          <DropdownMenuItem
            onClick={this.handleClearButtonClick}
            label="Clear area"
          />
        )}
      </DropdownMenu>
    );
  }
}
